/**
 * Character-Creator face-rig glue. Deliberately small surface area: the only
 * thing this module knows is how to take a canonical ARKit lip-sync weight
 * dict and fan it out to the morphs CC-derived rigs (Riya, Anne) actually
 * carry.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * CC exports differ from Avaturn / RPM in two ways that matter for lip sync:
 *
 *   1. CC ships **two parallel mouth-shape sets** — `Jaw_*` correctives
 *      (`Jaw_Open`, `Jaw_L`, `Jaw_R`, …) **and** a separate `V_*` viseme set
 *      for speech (`V_Open`, `V_Lip_Open`, `V_Tight`, `V_Wide`, `V_Tight_O`,
 *      `V_Dental_Lip`, …). The `V_*` shapes are the speech-authoring set.
 *      The `Jaw_L` / `Jaw_R` correctives literally **slide the jaw sideways**;
 *      driving them with ARKit `jawLeft` / `jawRight` will distort the face.
 *
 *   2. The `CC_Base_JawRoot` bone is **pre-rotated ~90°** in the bind pose.
 *      Driving its `rotation.x` from `jawOpen` rotates around the wrong local
 *      axis and **slides the jaw sideways** rather than opening it. The
 *      correct axis (measured per-character on Riya in the previous-chat
 *      workflow) is **local +Z**, with ~0.4 rad of opening — see
 *      `CC_JAW_OPEN_AXIS` / `CC_JAW_OPEN_RADIANS` exported below. The morph
 *      `Jaw_Open` alone moves vertices ~0.2 mm and is invisible without the
 *      bone hinge; the visible mouth motion is the bone, with the morph as a
 *      sub-pixel corrective.
 *
 * The avoided pitfalls — `Jaw_L`/`Jaw_R` (sideways slide), `rotation.x` on
 * the jaw bone (wrong axis), and forcing `Mouth_Close = 0` (fights the rest
 * pose) — produced "upper-lip-passes-the-lower-lip" / "stuck jaw" symptoms
 * in an earlier branch. This module deliberately avoids all three.
 *
 * ── What we do drive ───────────────────────────────────────────────────────
 *
 *   • `V_Open` + `V_Lip_Open`        — the genuine speech visemes.
 *   • `Jaw_Open`                     — the largest vertical opener (~2 cm
 *                                      delta on Riya's body mesh, vs ~0.8 cm
 *                                      for `V_Open`). Without this the mouth
 *                                      registers as only "slightly parted".
 *   • `V_Wide`                       — when the mouth is open + stretched
 *                                      (laughs / surprise) or smiling at low
 *                                      jaw weight.
 *   • `V_Tight` + `V_Tight_O`        — rounded shapes (funnel / pucker).
 *   • `V_Dental_Lip`                 — dental contact for closed consonants.
 *   • `Tongue_Out`                   — passthrough.
 *   • `mouthClose` (passthrough)     — the canonical key, written **only when
 *                                      explicitly present in input**. The
 *                                      morph-alias index resolves it to the
 *                                      rig's actual name. We never force it
 *                                      to zero (this is the bug from the
 *                                      earlier branch).
 *
 * ── Bleed safety ───────────────────────────────────────────────────────────
 *
 * Callers in `figure.ts` MUST gate this expansion on
 * `rigProfile === 'cc-base' || rigProfile === 'cc-base-noprefix'`. Non-CC
 * rigs receive the canonical ARKit dict unchanged. The `isCcBaseLipMorphName`
 * predicate is used in the per-frame mixer to identify lip-layer shapes for
 * CC rigs without running the canonical ARKit `LIP_SYNC_BLENDSHAPES` lookup
 * (which would never match `V_Open` anyway).
 */

/**
 * Expand canonical ARKit weights into the morph names CC rigs carry.
 *
 * Passes the input dict through unchanged (so non-lip canonicals — brows,
 * eyes, cheeks — keep flowing to the alias-resolved CC morph names) and
 * additively writes CC-specific lip morphs (`V_Open`, `V_Lip_Open`,
 * `Jaw_Open`, `V_Wide`, `V_Tight*`, `V_Dental_Lip`, `Tongue_Out`) when
 * the canonical lip weights cross visibility thresholds.
 *
 * This function is now used for ALL three face layers on CC rigs
 * (ambient, beat-emotion, viseme/lip-sync) — not just lip sync. Without
 * the fan-out on the emotion layer, an `EMOTION_PRESETS.surprise` with
 * `jawOpen: 0.45` would only drive Riya's `Jaw_Open` morph (which alone
 * barely visibly opens the mouth) and miss the `V_Open` + `V_Lip_Open`
 * lip-parting shapes that make the open mouth read.
 *
 * Returns a fresh dict — never mutates `arkit`.
 */
export function expandCcBaseVisemeWeights(
  arkit: Readonly<Record<string, number>>,
): Record<string, number> {
  // Pass through every input key so non-lip canonicals (browInnerUp,
  // eyeWideLeft, cheekSquintLeft, …) reach the alias-resolved CC morphs
  // via the mixer's `layerWeight` lookup. Without this, a preset like
  // `{ jawOpen: 0.45, browOuterUpLeft: 0.75 }` would lose the brow
  // contribution after fan-out.
  const out: Record<string, number> = { ...arkit };

  const jaw = arkit.jawOpen ?? 0;
  const funnel = arkit.mouthFunnel ?? 0;
  const pucker = arkit.mouthPucker ?? 0;
  const close = arkit.mouthClose ?? 0;
  const smile = Math.max(arkit.mouthSmileLeft ?? 0, arkit.mouthSmileRight ?? 0);
  const stretch = Math.max(arkit.mouthStretchLeft ?? 0, arkit.mouthStretchRight ?? 0);

  // Open mouth: drive both V_* speech visemes and Jaw_Open. V_Open alone is
  // too subtle on this export; Jaw_Open alone misses the lip-shape detail.
  if (jaw > 0.02) {
    out.V_Open = Math.max(out.V_Open ?? 0, jaw);
    out.V_Lip_Open = Math.max(out.V_Lip_Open ?? 0, jaw * 0.85);
    out.Jaw_Open = Math.max(out.Jaw_Open ?? 0, jaw);
  }

  // Wide-open + stretched (laughter, surprise).
  if (jaw > 0.35 && stretch > 0.1) {
    out.V_Wide = Math.max(out.V_Wide ?? 0, jaw * 0.45);
  }

  // Rounded shapes — V_Tight_O is the deeper round, V_Tight a softer one.
  if (funnel > 0.15 || pucker > 0.15) {
    const round = Math.max(funnel, pucker);
    out.V_Tight_O = Math.max(out.V_Tight_O ?? 0, round * 0.8);
    out.V_Tight = Math.max(out.V_Tight ?? 0, round * 0.5);
  }

  // Dental contact — only when close is meaningfully active. Pass mouthClose
  // through unchanged (alias index will resolve to `Mouth_Close` on CC).
  // Notably, we do NOT force mouthClose to zero when jaw opens — that was
  // the source of the upper-lip-overshoots-lower-lip bleed in the earlier
  // branch on rigs that keep some mouthClose active in bind.
  if (close > 0.2) {
    out.V_Dental_Lip = Math.max(out.V_Dental_Lip ?? 0, close * 0.85);
    out.mouthClose = close;
  }

  // Smiling at low jaw — pull the corners into V_Wide so the smile reads.
  if (smile > 0.2 && jaw < 0.45) {
    out.V_Wide = Math.max(out.V_Wide ?? 0, smile * 0.35);
  }

  if ((arkit.tongueOut ?? 0) > 0.1) {
    out.Tongue_Out = arkit.tongueOut!;
  }

  return out;
}

/**
 * Lip-layer shape predicate for CC rigs.
 *
 * Returns true for the morph names CC's lip-sync layer is allowed to drive.
 * Used by the per-frame morph mixer to mask the viseme layer to mouth-only
 * shapes (so a stray V_-keyed write can't influence brows / eyes / etc.).
 *
 * Deliberately does NOT include `Jaw_L` / `Jaw_R` (they slide the jaw
 * sideways) or `Mouth_*` corrective shapes (which the V_* set already covers
 * in CC's authoring conventions).
 */
export function isCcBaseLipMorphName(name: string): boolean {
  return /^V_/i.test(name)
    || /^Jaw_Open$/i.test(name)
    || /^Tongue_Out$/i.test(name);
}

// ── Jaw bone rotation ──────────────────────────────────────────────────────
//
// On Riya / CC GLB exports the visible mouth opening comes almost entirely
// from rotating `CC_Base_JawRoot`, NOT from the `Jaw_Open` morph. Per
// per-character measurement (cached in `Riya.morph-calibration.json` in the
// previous-chat workflow):
//
//   • The `Jaw_Open` morph moves body-mesh lip vertices ~0.2 mm — invisible.
//   • `CC_Base_JawRoot` rotated 0.4 rad around local +Z drops the jaw ~9 mm
//     in world space, with zero lateral drift (axis confirmed via child-bone
//     measurement: local +Z gives dy=−0.0094 at 20° vs >2 mm sideways for
//     local +X).
//   • The `Female_Angled` face overlay carries no morph data in Three.js
//     but its vertices ARE skinned to `CC_Base_JawRoot` — so the visible
//     face surface follows the bone rotation. (Stripping or hiding
//     Female_Angled would prevent any visible jaw motion.)
//
// CRITICAL: the axis must be local +Z, NOT local +X. An earlier branch
// drove `rotation.x` and produced the sideways-jaw skew + upper-lip-
// overshoot bleed. Local +Z is the safe axis on this rig.
export const CC_JAW_BONE_NAME_PATTERN = /^CC_Base_JawRoot$/i;
export const CC_JAW_OPEN_AXIS: readonly [number, number, number] = [0, 0, 1];
export const CC_JAW_OPEN_RADIANS = 0.4;
